/**
 * The Quotation Commission panel — the four commission rates, their total, the per-line markup,
 * and the additional discount.
 *
 * Modelled on the ERP screen (Akhil, 2026-09-04). Every field here is real on the live `Quotation`
 * doctype; this app previously wrote one of them.
 *
 * ---- Markup applies to LINES, and that is why it is a button ----
 *
 * "This screen basically updates the markup for each line item." Setting 31% here reprices every
 * unlocked line from its own cost. That is a bulk edit over the whole document, so it is an
 * explicit action rather than something that happens as digits are typed — a markup field that
 * repriced on every keystroke would rewrite the quotation three times on the way from 3 to 31.
 *
 * Locked lines are skipped. The count is shown on the button so nobody applies a markup believing
 * it covered everything.
 *
 * ---- Discount ----
 *
 * Percentage and amount are one figure two ways (`lib/additionalDiscount.ts`). Typing either
 * derives the other; only the one actually typed is sent, so the server's own derivation is never
 * overwritten with our rounding of it.
 */

import { useState } from 'react'
import { Card, Button } from '../../components/ui'
import { formatMoney } from '../../lib/format'
import {
  totalCommissionPct,
  commissionAmount,
  effectiveMarkupPct,
  commissionRatesPatch,
  commissionSplitUnrecorded,
  seedCommissionRates,
  type CommissionRates,
} from '../../lib/quotationCommission'
import { basisChangePatch, discountCommitPatch, leadingDiscount, resolveDiscount, discountedTotal, type DiscountBasis } from '../../lib/additionalDiscount'
import { discountBases } from '../../lib/derivedFigures'
import { viewMoney } from '../../lib/viewMoney'
import type { WireFx } from '../../lib/api/wire'
import type { Currency, Quotation } from '../../lib/types'

/** A percentage input that keeps its own text, so a half-typed "1." is not parsed to 1 mid-edit. */
function PctField({
  label,
  value,
  readOnly,
  onCommit,
}: {
  label: string
  value: number
  /**
   * A REAL disable — the document is no longer editable. Never set from "a save is in flight":
   * see the panel's own note on why that breaks every field at once.
   */
  readOnly?: boolean
  onCommit: (n: number) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft ?? String(value)

  return (
    <label className="ef" style={{ minWidth: 0 }}>
      <span>{label}</span>
      <input
        type="number"
        step="0.01"
        value={shown}
        disabled={readOnly}
        onChange={(e) => setDraft(e.target.value)}
        // Committed on blur, never per keystroke: each commit is a write, and "1" on the way to
        // "15" is not a rate anybody chose.
        onBlur={() => {
          const next = Number(draft)
          setDraft(null)
          if (draft !== null && Number.isFinite(next) && next !== value) onCommit(next)
        }}
      />
    </label>
  )
}

export function CommissionPanel({
  quotation,
  displayCurrency,
  fx,
  saving,
  readOnly,
  onCommitTerms,
  lockedCount,
  onApplyMarkup,
}: {
  quotation: Quotation
  /**
   * The currency the PICKER shows — a view. Every figure in this panel is the document's own;
   * it is converted for display and a typed amount converted back (`viewMoney`), the way a line
   * price already is. Labelling the document figure with the view currency unconverted showed
   * "133 AED" as "$133" (Akhil, 2026-09-17).
   */
  displayCurrency: Currency
  fx: WireFx
  /**
   * A write is in flight. DIMS the panel; it must never disable the fields.
   *
   * Every field here commits on blur, so committing one sets this true — and disabling on it
   * meant that clicking from Management into Software landed on an input the browser had just
   * disabled. The second field could not be typed in at all, which is exactly what "commission is
   * not updating" looked like. Worse, disabling a field that HAS focus force-blurs it, and that
   * blur is indistinguishable from the user finishing their edit: it commits whatever partial
   * digits were in the box.
   *
   * The totals panel this replaced carried a comment saying precisely this. Rewriting the fields
   * lost the warning with it.
   */
  saving: boolean
  /** The document is not editable at all — a sent quotation. This DOES disable. */
  readOnly: boolean
  onCommitTerms: (terms: Record<string, number | string>) => void
  /** Lines the markup will skip — stated on the button, not discovered afterwards. */
  lockedCount: number
  /** Reprices every unlocked line from its own cost. The rates are NOT written to the document. */
  onApplyMarkup: (pct: number, commissionPct: number) => void
}) {
  const { commission, discount, totals } = quotation
  const view = viewMoney(totals.total.currency, displayCurrency, fx)
  /*
    Collapsed by default. This panel is set once or twice per quotation and then sits above the
    lines for the rest of the session, pushing the table — the thing being worked on — down the
    page. Closed it still states what is in force, so nothing is hidden, only folded.
  */
  const [open, setOpen] = useState(false)
  const [markupDraft, setMarkupDraft] = useState<string | null>(null)
  const markup = markupDraft === null ? commission.markup : Number(markupDraft)

  /*
    The rates now PERSIST — `line_markup` and `line_commission`, added 2026-09-07.

    They used to be session-only, because the only fields available (`markup` and the four
    commission rates) are what the server BILLS from: storing 30 there charged the customer 30 on
    top of line prices that already contained it (`D27`). The two new fields are inert storage —
    measured on QTN-2026-00012, writing 30/40 left `grand_total` equal to `net_total` — so the
    purchaser's rates finally survive a reload.

    The four individual rates persist too, since 2026-09-18 (Akhil: "not saving in backend").
    They were session-only while the server billed from them; measured that day it no longer
    does — see `commissionRatesPatch`. Every commit writes the four AND their total; a document
    from before opens on its total under Management (`seedCommissionRates`).
  */
  const [rateDrafts, setRateDrafts] = useState<CommissionRates | null>(null)
  const rates: CommissionRates = rateDrafts ?? seedCommissionRates(commission)
  // Only until the purchaser types the real split — then the four are on the document.
  const splitUnrecorded = rateDrafts === null && commissionSplitUnrecorded(commission)
  const setRate = (key: keyof CommissionRates, n: number) => {
    const next = { ...rates, [key]: n }
    setRateDrafts(next)
    onCommitTerms(commissionRatesPatch(next))
  }
  // Previewed from the four on screen rather than read off `total_commission`: the server's figure
  // lags an edit in flight, and a total that disagrees with the numbers above it reads as a bug.
  const previewTotalPct = totalCommissionPct(rates)

  /*
    What the LINES say is applied, for when the session's own rates are gone.

    The rates cannot be stored (`effectiveMarkupPct`'s own note — the fields that would hold them
    are the fields that trigger the server's charge), so a reload, or coming back from
    `/revisions`, leaves this panel at 0% over a quotation whose every line carries a markup.
    Reporting 0 there is worse than reporting nothing: it invites someone to "fix" it by applying
    a markup on top of one already in the prices.
  */
  /*
    The stored rates are now the truth, so the derived figure is only a FALLBACK — for a quotation
    priced before `line_markup`/`line_commission` existed, whose rates were never recorded
    anywhere but the prices themselves.
  */
  const applied = effectiveMarkupPct(quotation.lines)
  const ratesAreSet = markup > 0 || previewTotalPct > 0

  /*
    Either figure may lead (Akhil, 2026-09-15): a purchaser types a percentage OR an amount, and
    the other follows. The document stores both and the server derives neither (measured — pct 5
    left the amount at 0, amount 50 left the pct at 0), so whichever is typed, BOTH are sent,
    resolved here against the same totals the panel shows. On read, a stored percentage leads;
    an amount with no percentage is an amount-led discount.
  */
  // "Grand Total" is the total WITH taxes — see `discountBases`. It used to be `totals.total`, the
  // untaxed subtotal, so this panel's amount disagreed with the totals panel whenever a tax row
  // existed, and switching the basis changed nothing here (Akhil, 2026-09-16).
  const discountTotals = discountBases(quotation)
  // A stored percentage leads, against the CURRENT base — so a reprice moves the amount, never
  // the rate the purchaser set (2026-09-18). See `leadingDiscount`.
  const resolved = resolveDiscount(leadingDiscount(discount) ?? { mode: 'pct', value: 0 }, discountTotals, discount.apply_on)
  const commitDiscount = (input: { mode: 'pct' | 'amount'; value: number }) => {
    onCommitTerms({
      ...discountCommitPatch(input, discountTotals, discount.apply_on),
      // The basis the figures were computed against goes WITH them when the document has none
      // stored yet — otherwise the JSON carries a discount and no `apply_discount_on`.
      ...(discount.apply_on_stored ? {} : { apply_discount_on: discount.apply_on }),
    })
  }

  return (
    <Card style={{ marginBottom: 14, ...(saving ? { opacity: 0.6 } : {}) }}>
      {/* The whole header toggles — a caret alone is a small target for something used this often. */}
      <div
        role="button"
        // Still opens when the page is read-only — see `ReadOnlyGate`.
        data-readonly-allow=""
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
      >
        <b>💰 Quotation commission</b>
        {/*
          What is in force, readable while closed. A collapsed panel that says nothing is a panel
          people forget is set — and these numbers decide the customer's price.
        */}
        <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
          {/*
            The figure only — no explanation. Applying again reprices from COST (`applyMarkup`), so
            re-entering a rate over an already-marked-up quotation replaces the uplift rather than
            compounding it; the warning that used to sit here was describing a hazard that the
            idempotence removes.
          */}
          {!ratesAreSet && applied !== null && applied > 0 ? (
            <>
              lines carry <b style={{ color: 'var(--ink)' }}>{applied}%</b> over cost
            </>
          ) : (
            <>
              markup <b style={{ color: 'var(--ink)' }}>{markup}%</b> · commission{' '}
              <b style={{ color: 'var(--ink)' }}>{previewTotalPct}%</b>
            </>
          )}
          {previewTotalPct > 0 ? (
            <>
              {' '}
              (
              {formatMoney({
                amount: view.toView(commissionAmount(totals.sell_subtotal.amount, rates)),
                currency: view.currency,
              })}
              )
            </>
          ) : null}
          {resolved.amount > 0 ? (
            <>
              {' '}
              · discount <b style={{ color: 'var(--ink)' }}>{resolved.pct}%</b> on {discount.apply_on}
            </>
          ) : null}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--muted)' }}>{open ? '▴' : '▾'}</span>
      </div>

      {!open ? null : (
        <>
      <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '10px 0 12px' }}>
        Commission rates and the markup that price each line. Applied to the LINE prices — the
        quotation total is their sum, with nothing added on top.

      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <PctField
          label="Management %"
          value={rates.management}
          readOnly={readOnly}
          onCommit={(n) => setRate('management', n)}
        />
        <PctField
          label="Software %"
          value={rates.software}
          readOnly={readOnly}
          onCommit={(n) => setRate('software', n)}
        />
        <PctField
          label="Port agent %"
          value={rates.port_agent}
          readOnly={readOnly}
          onCommit={(n) => setRate('port_agent', n)}
        />
        <PctField
          label="Other charges %"
          value={rates.other_charges}
          readOnly={readOnly}
          onCommit={(n) => setRate('other_charges', n)}
        />

        {/*
          Two different quantities, and the panel used to show one under the other's name.

          The four rates SUM to a percentage; the server's `total_commission` field is a Currency
          AMOUNT — `net × that percentage`, added to what the customer pays alongside the markup.
          Labelling the summed rate "Total commission" put a percent where a document field of that
          name holds money.

          Computed here rather than read off the document so it tracks an edit in flight: the
          server's figure is one round trip behind while a rate is being changed.
        */}
        <div className="ef" style={{ minWidth: 0 }}>
          <span>Commission ({previewTotalPct}% of net)</span>
          <div
            className="mono"
            style={{ padding: '7px 9px', background: 'var(--bg)', borderRadius: 8, fontWeight: 700 }}
            title={`${previewTotalPct}% of the ${formatMoney(totals.sell_subtotal)} net — added to the customer's total alongside the markup.`}
          >
            {formatMoney({ amount: view.toView(commissionAmount(totals.sell_subtotal.amount, rates)), currency: view.currency })}
          </div>
        </div>
      </div>
      {splitUnrecorded ? (
        <div style={{ color: 'var(--warn)', fontSize: 12.5, marginTop: 6 }}>
          ⚠ {commission.line_commission}% is the <b>total</b> commission on file — its split across the four was never
          recorded (priced before the rates were stored), so it is shown under Management only to keep the rate. Enter the
          actual rates and they will be saved.
        </div>
      ) : null}

      {/* ---- markup: a bulk edit, so an explicit action ---- */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          marginTop: 14,
          paddingTop: 12,
          borderTop: '1px solid var(--line)',
        }}
      >
        <label className="ef" style={{ width: 130 }}>
          <span>Markup %</span>
          <input
            type="number"
            step="0.01"
            value={markupDraft ?? String(commission.markup)}
            readOnly={readOnly}
            onChange={(e) => setMarkupDraft(e.target.value)}
          />
        </label>
        <Button
          variant="primary"
          disabled={saving || readOnly || !Number.isFinite(markup)}
          onClick={() => {
            /*
              ONE write, and only one.

              This used to also reprice every line at `cost x (1 + markup) / (1 - commission)`.
              That double-charged, because the server ALREADY adds both on top of the line
              subtotal: `grand_total = net x (1 + markup/100) + convenience + net x commission/100`
              (`pricing.ts`'s `previewTotals`, mirroring the stored total). Measured on a live
              quotation: cost 28.00 became a line price of 34.22 (28 x 1.1 / 0.9), and the totals
              panel then added 10% markup and 10% commission to the subtotal AGAIN — 800.00 of
              cost billed as 1,173.30 instead of 960.00.

              The gross-up was wrong on its own terms too. Grossing up is right only when
              commission is taken OUT of the selling price; this server ADDS it to what the
              customer pays, so the business already receives the full subtotal.
            */
            /*
              Both writes: the line prices, and the RATES that produced them — the markup, the
              four commissions and their total (2026-09-18). `markup` itself (the document-level
              charge) stays 0.
            */
            onCommitTerms({ line_markup: markup, ...commissionRatesPatch(rates) })
            onApplyMarkup(markup, previewTotalPct)
            setMarkupDraft(null)
          }}
        >
          Apply to every line
        </Button>
        <span style={{ color: 'var(--muted)', fontSize: 12, flex: '1 1 220px' }}>
          {/* The formula, stated — a caption saying only "30% over cost" describes a number the
              button does not produce once commission is in play. */}
          Reprices each line at {Number.isFinite(markup) ? markup : commission.markup}% over its own
          cost
          {previewTotalPct > 0 ? (
            <>
              , grossed up for {previewTotalPct}% commission (÷ {((100 - previewTotalPct) / 100).toFixed(2)})
            </>
          ) : null}
          . The quotation total is the sum of those lines — nothing is added on top.
          {lockedCount > 0 ? (
            <>
              {' '}
              <b style={{ color: 'var(--warn)' }}>{lockedCount} locked line(s) will be skipped.</b>
            </>
          ) : null}
        </span>
      </div>

      {/* ---- additional discount ---- */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
        <b style={{ fontSize: 13 }}>Additional discount</b>
        {view.missingRate ? (
          <div style={{ color: 'var(--warn)', fontSize: 12.5, marginTop: 4 }}>
            No {totals.total.currency} → {displayCurrency} rate on file — amounts here are still {totals.total.currency}.
          </div>
        ) : null}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 8 }}>
          <label className="ef" style={{ minWidth: 0 }}>
            <span>Apply on</span>
            <select
              // `''` while the document stores no basis (Akhil, 2026-09-16): the panel used to
              // show "Grand Total" as if it were set, so choosing it fired no change and nothing
              // was written. The unsaved state is its own option, and picking either real value
              // stores it.
              value={discount.apply_on_stored ? discount.apply_on : ''}
              disabled={readOnly}
              onChange={(e) => {
                if (e.target.value === '') return
                // The basis AND a cleared discount (Akhil, 2026-09-17): a figure set against
                // the grand total is not the same discount against the net, so the purchaser
                // types it again rather than having it silently re-based. See `basisChangePatch`.
                onCommitTerms(basisChangePatch(e.target.value as DiscountBasis))
              }}
            >
              {!discount.apply_on_stored ? <option value="">Grand Total (ERP default — not saved)</option> : null}
              {/* Exactly the doctype's two Select values — anything else fails validation. */}
              <option value="Grand Total">Grand Total</option>
              <option value="Net Total">Net Total</option>
            </select>
          </label>
          <PctField
            label="Discount %"
            value={resolved.pct}
            readOnly={readOnly}
            onCommit={(n) => commitDiscount({ mode: 'pct', value: n })}
          />
          <PctField
            label={`Discount amount (${view.currency})`}
            value={view.toView(resolved.amount)}
            readOnly={readOnly}
            // Typed in the view currency, stored in the document's.
            onCommit={(n) => commitDiscount({ mode: 'amount', value: view.fromView(n) })}
          />
          <div className="ef" style={{ minWidth: 0 }}>
            <span>After discount</span>
            <div
              className="mono"
              style={{ padding: '7px 9px', background: 'var(--bg)', borderRadius: 8, fontWeight: 700 }}
            >
              {formatMoney({ amount: view.toView(discountedTotal(resolved.base, resolved.amount)), currency: view.currency })}
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </Card>
  )
}
